import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { blogAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export default function BlogPostPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [comment, setComment] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => blogAPI.getOne(slug).then((res) => res.data),
    keepPreviousData: true,
  });

  const addComment = useMutation({
    mutationFn: (payload) => blogAPI.addComment(data.id, payload),
    onSuccess: () => {
      toast.success('Komentari u dërgua dhe do të miratohet');
      setComment('');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Gabim gjatë dërgimit të komentit'),
  });

  if (isLoading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (isError || !data) return (
    <div className="page-container" style={{ paddingTop: 80, paddingBottom: 80, textAlign: 'center' }}>
      <p style={{ color: 'var(--text-light)', marginBottom: 16 }}>Artikulli nuk mund të gjendet.</p>
      <Link to="/blog" className="btn btn-primary">Kthehu tek Blog</Link>
    </div>
  );

  const post = data;
  const related = post.related || [];

  return (
    <div className="page-container" style={{ paddingTop: 80, paddingBottom: 80 }}>
      <div style={{ marginBottom: 24 }}>
        <Link to="/blog" className="btn btn-ghost">← Kthehu tek Blog</Link>
      </div>
      <article className="blog-post-hero">
        {post.cover_image && <img src={post.cover_image} alt={post.title} />}
        <div className="blog-post-hero-text">
          <div className="section-eyebrow">Blog</div>
          <h1>{post.title}</h1>
          <div style={{ color: 'var(--text-light)', marginTop: 12 }}>
            {new Date(post.published_at).toLocaleDateString('sq')} · {post.author_name || 'Admin'}
          </div>
        </div>
      </article>

      <div className="blog-post-content">
        {post.content?.split('\n\n').map((paragraph, idx) => (
          <p key={idx}>{paragraph}</p>
        ))}
      </div>

      <section style={{ marginTop: 40 }}>
        <h2 className="section-title" style={{ marginBottom: 18 }}>Komentet</h2>
        {post.comments?.length > 0 ? (
          post.comments.map((commentItem) => (
            <div key={commentItem.id} className="comment-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <strong>{commentItem.user_name || commentItem.guest_name || 'Përdorues'}</strong>
                <span style={{ color: 'var(--text-light)', fontSize: '.85rem' }}>{new Date(commentItem.created_at).toLocaleDateString('sq')}</span>
              </div>
              <p style={{ marginTop: 10, lineHeight: 1.75 }}>{commentItem.body}</p>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">💬</div>
            <h3>Nuk ka komente ende</h3>
            <p>Jini i pari që ndani mendimin tuaj.</p>
          </div>
        )}
      </section>

      <section style={{ marginTop: 40 }}>
        <h2 className="section-title" style={{ marginBottom: 18 }}>Shto koment</h2>
        <div className="card" style={{ padding: 24 }}>
          <p style={{ marginBottom: 14, color: 'var(--text-light)' }}>Komenti juaj do të dërgohet për miratim.</p>
          <textarea
            className="form-input"
            rows={5}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Shkruaj koment..."
          />
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              if (!comment.trim()) {
                toast.error('Shkruaj koment para se ta dërgosh');
                return;
              }
              addComment.mutate({ body: comment, name: user?.name, email: user?.email });
            }}
          >
            Dërgo koment
          </button>
        </div>
      </section>

      {related.length > 0 && (
        <section style={{ marginTop: 40 }}>
          <h2 className="section-title" style={{ marginBottom: 18 }}>Postimet e tjera</h2>
          <div className="related-grid">
            {related.map((item) => (
              <Link key={item.id} to={`/blog/${item.slug}`} className="related-card">
                <strong>{item.title}</strong>
                <span style={{ color: 'var(--text-light)', fontSize: '.9rem' }}>{new Date(item.published_at).toLocaleDateString('sq')}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <style>{`
        .blog-post-hero { display: grid; grid-template-columns: 1.2fr .8fr; gap: 28px; align-items: center; }
        .blog-post-hero img { width: 100%; border-radius: var(--radius-xl); object-fit: cover; max-height: 420px; }
        .blog-post-hero-text { display: flex; flex-direction: column; gap: 16px; }
        .blog-post-content { color: var(--text-mid); line-height: 1.9; margin-top: 32px; }
        .blog-post-content p { margin-bottom: 18px; }
        .comment-card { background: white; border-radius: var(--radius-xl); border: 1px solid var(--cream-dark); padding: 22px; margin-bottom: 18px; }
        .related-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; }
        .related-card { display: block; padding: 20px; border-radius: var(--radius-xl); border: 1px solid var(--cream-dark); color: inherit; }
        .related-card:hover { border-color: var(--green-mid); }
        @media(max-width: 980px) { .blog-post-hero { grid-template-columns: 1fr; } .related-grid { grid-template-columns: 1fr 1fr; } }
        @media(max-width: 680px) { .related-grid { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}
